import { UploadFile } from 'antd/lib/upload/interface';
import { API, graphqlOperation } from 'aws-amplify';
import {
  createProductVideoKey,
  deleteProductVideoKey,
} from '../../graphql/mutations';
import { Entity } from '~core/domain/Entity';
import { Video } from '~domain/file/Video';
import { PartialOptionaled } from '~types/utils';
import { getImageUrl } from '~utils/image';

export type ProductVideoType = {
  videoKey: string;
  product_id: string;
  product_brand_id: string;
  product_owner: string;
  video: Video;
};

type ProductVideoJson = Omit<ProductVideoType, 'video' | 'thumbUrl'> &
  PartialOptionaled<UploadFile, 'size' | 'type'>;

export class ProductVideo extends Entity<ProductVideoType> {
  static limitSize = 31457280;
  static shortenLimitSize = `${ProductVideo.limitSize / 1024 ** 2}MB`;

  private constructor(params: ProductVideoType) {
    super(params);
  }

  static async create({
    uid,
    size,
    url,
    name,
    type,
    ...params
  }: ProductVideoJson) {
    const video = await Video.create({
      uid,
      name,
      url,
      size: size ?? 0,
      type: type ?? 'video',
      directory: ProductVideo.getDirectory({
        product_brand_id: params.product_brand_id,
        product_id: params.product_id,
      }),
      originFileObj: params.originFileObj,
    });

    return new ProductVideo({
      ...params,
      video,
    });
  }

  static async createByVideoKey({
    videoKey,
    uid,
    product_brand_id,
    product_id,
    product_owner,
  }: Pick<
    ProductVideoJson,
    'videoKey' | 'uid' | 'product_brand_id' | 'product_id' | 'product_owner'
  >) {
    const url = await getImageUrl(
      `${ProductVideo.getDirectory({
        product_brand_id,
        product_id,
      })}${videoKey}`
    );

    return await ProductVideo.create({
      product_id,
      product_brand_id: product_brand_id,
      product_owner: product_owner,
      uid: uid,
      name: videoKey,
      status: 'done',
      url: url as string,
      videoKey: videoKey,
    });
  }

  static getPath(brandId: string, productId: string, videoKey: string) {
    return `${ProductVideo.getDirectory({
      product_brand_id: brandId,
      product_id: productId,
    })}${videoKey}`;
  }

  private static getDirectory(params: {
    product_brand_id: string;
    product_id: string;
  }) {
    return `videos/product/${params.product_brand_id}/${params.product_id}/`;
  }

  private get directory() {
    return ProductVideo.getDirectory({
      product_brand_id: this.product_brand_id,
      product_id: this.product_id,
    });
  }

  get uid() {
    return this.video.uid;
  }

  get size() {
    return this.video.size;
  }

  get name() {
    return this.video.name;
  }

  get type() {
    return this.video.type;
  }

  get url() {
    return this.video.url;
  }

  get thumbUrl() {
    return this.video.thumbUrl;
  }

  async upload() {
    const key = await this.video.upload();
    const directory = ProductVideo.getDirectory({
      product_brand_id: this.product_brand_id,
      product_id: this.product_id,
    });
    const fileName = key.replace(directory, '');
    return await ProductVideo.create({
      ...this.toJson(),
      name: fileName,
      videoKey: fileName,
    });
  }

  async register() {
    return API.graphql<any>(
      graphqlOperation(createProductVideoKey, {
        input: {
          videoKey: this.videoKey,
          product_id: this.product_id,
          owner: this.product_owner,
        },
      })
    );
  }

  async delete() {
    await this.video.delete();
    return await API.graphql<any>(
      graphqlOperation(deleteProductVideoKey, {
        input: {
          id: this.video.uid,
        },
      })
    );
  }

  toJson(): ProductVideoJson {
    return {
      videoKey: this.videoKey,
      product_id: this.product_id,
      product_brand_id: this.product_brand_id,
      product_owner: this.product_owner,
      uid: this.video.uid,
      size: this.video.size,
      name: this.video.name,
      fileName: this.video.fileName,
      lastModified: this.video.lastModified,
      lastModifiedDate: this.video.lastModifiedDate,
      url: this.video.url,
      status: this.video.status,
      percent: this.video.percent,
      originFileObj: this.video.originFileObj,
      response: this.video.response,
      error: this.video.error,
      linkProps: this.video.linkProps,
      type: this.video.type,
      xhr: this.video.xhr,
      preview: this.video.preview,
    };
  }
}
